import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams, useParams, Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2,
  Send,
  AlertCircle,
  Check,
  CheckCheck,
  Clock,
  Inbox,
  ExternalLink,
  ArrowLeft,
  Search,
  Mail,
  MailOpen,
  MoreHorizontal,
  ChevronDown,
} from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface SMSMessage {
  _id: string;
  body: string;
  from: string;
  to: string;
  received?: boolean;
  createdAt: string;
  read?: boolean;
  status?: string;
  errorCode?: string;
  errorMessage?: string;
  contactLead?: string;
  lead?: {
    firstName?: string;
    lastName?: string;
  };
}

function smsOutboundFailed(message: SMSMessage) {
  if (message.received) return false;
  const s = (message.status ?? "").toLowerCase();
  return s === "failed" || s === "undelivered" || Boolean(message.errorCode);
}

function SMSMessageStatusIcon({
  message,
  variant,
}: {
  message: SMSMessage;
  variant: "received" | "sent";
}) {
  const muted =
    variant === "received"
      ? "text-muted-foreground"
      : "text-primary-foreground/70";
  const s = (message.status ?? "").toLowerCase();

  if (message.received) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex ${muted}`} aria-label="Received">
            <Inbox className="h-3.5 w-3.5 shrink-0" />
          </span>
        </TooltipTrigger>
        <TooltipContent>Received</TooltipContent>
      </Tooltip>
    );
  }

  if (smsOutboundFailed(message)) {
    const code =
      message.errorCode?.trim() ||
      message.status?.trim() ||
      "Delivery failed";
    const extra = message.errorMessage?.trim();
    const tooltip = extra ? `${code}\n${extra}` : code;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex text-amber-200 shrink-0"
            aria-label={`Delivery error: ${code}`}
          >
            <AlertCircle className="h-3.5 w-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="left"
          className="max-w-xs whitespace-pre-wrap font-mono text-xs"
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (s === "delivered" || s === "read") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex ${muted}`} aria-label="Delivered">
            <CheckCheck className="h-3.5 w-3.5 shrink-0" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="capitalize">
          {message.status || "Delivered"}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (s === "sent") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex ${muted}`} aria-label="Sent">
            <Check className="h-3.5 w-3.5 shrink-0" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="left">Sent</TooltipContent>
      </Tooltip>
    );
  }

  if (
    s === "queued" ||
    s === "accepted" ||
    s === "sending" ||
    s === "scheduled"
  ) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex ${muted}`} aria-label="Pending">
            <Clock className="h-3.5 w-3.5 shrink-0" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="capitalize">
          {message.status || "Pending"}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={`inline-flex ${muted}`} aria-label="Sent">
          <Check className="h-3.5 w-3.5 shrink-0" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="left" className="capitalize">
        {message.status?.trim() ? message.status : "Sent"}
      </TooltipContent>
    </Tooltip>
  );
}

const CONVERSATION_PAGE_SIZE = 30;

type InboxListFilter = "unread" | "all" | "optouts";

interface ConversationSummary {
  phoneNumber: string;
  hasUnread: boolean;
  lastAt?: string;
  lastBody?: string;
  contactLead?: string;
  lead?: {
    firstName?: string;
    lastName?: string;
  };
}

function formatConversationLastAt(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function toggleSetValue({
  current,
  value,
}: {
  current: Set<string>;
  value: string;
}) {
  const next = new Set(current);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
}

function InboxReadActionsMenu({
  disabled,
  updating,
  onMarkRead,
  onMarkUnread,
}: {
  disabled: boolean;
  updating: boolean;
  onMarkRead: () => void;
  onMarkUnread: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={disabled || updating}
        >
          {updating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MoreHorizontal className="h-4 w-4" />
          )}
          Actions
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem
          disabled={disabled || updating}
          onSelect={() => onMarkRead()}
        >
          <MailOpen className="h-4 w-4" />
          Mark as Read
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={disabled || updating}
          onSelect={() => onMarkUnread()}
        >
          <Mail className="h-4 w-4" />
          Mark as Unread
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SMSInbox() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { phoneNumber: phoneFromPath } = useParams<{ phoneNumber?: string }>();
  const selectedPhoneNumber =
    phoneFromPath != null && phoneFromPath !== "" ? phoneFromPath : null;
  const isListOnly = selectedPhoneNumber == null;
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [threadMessages, setThreadMessages] = useState<SMSMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [inboxListFilter, setInboxListFilter] =
    useState<InboxListFilter>("unread");
  const [conversationListLoading, setConversationListLoading] = useState(false);
  const [conversationSearch, setConversationSearch] = useState("");
  const [appliedConversationSearch, setAppliedConversationSearch] =
    useState("");
  const [markingUnread, setMarkingUnread] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [loadingMoreConversations, setLoadingMoreConversations] =
    useState(false);
  const [selectedConversationPhones, setSelectedConversationPhones] = useState<
    Set<string>
  >(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [selectingAllConversations, setSelectingAllConversations] =
    useState(false);
  const isFirstConversationListFetch = useRef(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationListScrollRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const conversationsRef = useRef<ConversationSummary[]>([]);
  const conversationPageRef = useRef(1);
  const hasMoreConversationsRef = useRef(false);
  const loadingMoreConversationsRef = useRef(false);
  const inboxListFilterRef = useRef(inboxListFilter);
  const appliedConversationSearchRef = useRef(appliedConversationSearch);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    inboxListFilterRef.current = inboxListFilter;
  }, [inboxListFilter]);

  useEffect(() => {
    appliedConversationSearchRef.current = appliedConversationSearch;
  }, [appliedConversationSearch]);

  const fetchConversations = useCallback(
    async ({
      filter,
      page,
      limit = CONVERSATION_PAGE_SIZE,
      replace,
      search,
    }: {
      filter: InboxListFilter;
      page: number;
      limit?: number;
      replace: boolean;
      search: string;
    }) => {
      const params = new URLSearchParams({
        listFilter: filter,
        page: String(page),
        limit: String(limit),
      });
      const trimmedSearch = search.trim();
      if (trimmedSearch) {
        params.set("search", trimmedSearch);
      }
      const response = await fetch(`/api/sms-messages?${params}`);
      if (response.ok) {
        const data = await response.json();
        const fetchedConversations: ConversationSummary[] =
          data.conversations || [];
        const more = Boolean(data.hasMore);
        if (replace) {
          setConversations(fetchedConversations);
          conversationPageRef.current = Math.max(
            1,
            Math.ceil(fetchedConversations.length / CONVERSATION_PAGE_SIZE)
          );
        } else {
          setConversations((prev) => {
            const seen = new Set(prev.map((c) => c.phoneNumber));
            const appended = fetchedConversations.filter(
              (c) => !seen.has(c.phoneNumber)
            );
            return [...prev, ...appended];
          });
          conversationPageRef.current = page;
        }
        setHasMoreConversations(more);
        hasMoreConversationsRef.current = more;
        return fetchedConversations;
      }
      return [];
    },
    []
  );

  async function refreshLoadedConversations({
    filter,
  }: {
    filter: InboxListFilter;
  }) {
    const loadedCount = conversationsRef.current.length;
    const pageCount = Math.max(
      1,
      Math.ceil(loadedCount / CONVERSATION_PAGE_SIZE)
    );
    await fetchConversations({
      filter,
      page: 1,
      limit: pageCount * CONVERSATION_PAGE_SIZE,
      replace: true,
      search: appliedConversationSearchRef.current,
    });
  }

  useEffect(() => {
    const legacyPhone = searchParams.get("phone");
    if (legacyPhone && isListOnly) {
      navigate(
        `/messages/sms/inbox/${encodeURIComponent(legacyPhone)}`,
        { replace: true }
      );
    }
  }, [searchParams, isListOnly, navigate]);

  useEffect(() => {
    const crumbs = [
      { label: "Messages", path: "/messages" },
      { label: "SMS", path: "/messages/sms" },
      { label: "Inbox", path: "/messages/sms/inbox" },
    ];
    if (selectedPhoneNumber != null) {
      crumbs.push({
        label: selectedPhoneNumber,
        path: `/messages/sms/inbox/${encodeURIComponent(selectedPhoneNumber)}`,
      });
    }
    setBreadcrumbs(crumbs);
    return () => {
      clearBreadcrumbs();
    };
  }, [selectedPhoneNumber]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const initial = isFirstConversationListFetch.current;
      if (initial) {
        setIsLoading(true);
      } else {
        setConversationListLoading(true);
      }
      try {
        conversationPageRef.current = 1;
        hasMoreConversationsRef.current = false;
        setSelectedConversationPhones(new Set());
        await fetchConversations({
          filter: inboxListFilter,
          page: 1,
          replace: true,
          search: appliedConversationSearch,
        });
      } catch (error) {
        console.error("Failed to fetch conversations:", error);
      } finally {
        if (cancelled) {
          return;
        }
        if (initial) {
          setIsLoading(false);
          isFirstConversationListFetch.current = false;
        } else {
          setConversationListLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inboxListFilter, appliedConversationSearch, fetchConversations]);

  useEffect(() => {
    if (!isListOnly) return;
    const sentinel = loadMoreSentinelRef.current;
    const root = conversationListScrollRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasMoreConversationsRef.current) return;
        if (loadingMoreConversationsRef.current) return;

        loadingMoreConversationsRef.current = true;
        setLoadingMoreConversations(true);
        void (async () => {
          try {
            await fetchConversations({
              filter: inboxListFilterRef.current,
              page: conversationPageRef.current + 1,
              replace: false,
              search: appliedConversationSearchRef.current,
            });
          } catch (error) {
            console.error("Failed to load more conversations:", error);
          } finally {
            loadingMoreConversationsRef.current = false;
            setLoadingMoreConversations(false);
          }
        })();
      },
      { root, rootMargin: "80px" }
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [
    isListOnly,
    conversationListLoading,
    conversations.length,
    loadingMoreConversations,
    hasMoreConversations,
    fetchConversations,
  ]);

  useEffect(() => {
    if (selectedPhoneNumber == null || selectedPhoneNumber === "") {
      setThreadMessages([]);
      setThreadLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setThreadLoading(true);
      try {
        const response = await fetch(
          `/api/sms-messages?phoneNumber=${encodeURIComponent(selectedPhoneNumber)}`
        );
        if (cancelled || !response.ok) return;
        const data = await response.json();
        setThreadMessages(data.messages || []);
      } catch (e) {
        console.error("Failed to load thread:", e);
        if (!cancelled) setThreadMessages([]);
      } finally {
        if (!cancelled) setThreadLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedPhoneNumber]);

  async function handleSend() {
    const phone = selectedPhoneNumber ?? "";
    if (!phone || !replyText.trim() || isSending) return;
    setIsSending(true);
    try {
      const response = await fetch("/api/sms-messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: phone, body: replyText.trim() }),
      });
      if (response.ok) {
        setReplyText("");
        await refreshLoadedConversations({ filter: inboxListFilter });
        if (phone) {
          const threadRes = await fetch(
            `/api/sms-messages?phoneNumber=${encodeURIComponent(phone)}`
          );
          if (threadRes.ok) {
            const threadData = await threadRes.json();
            setThreadMessages(threadData.messages || []);
          }
        }
      } else {
        const data = await response.json();
        alert(data.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Failed to send:", error);
      alert("Failed to send message");
    } finally {
      setIsSending(false);
    }
  }

  async function handleMarkUnread() {
    const phone = selectedPhoneNumber ?? "";
    if (!phone || !canMarkConversationUnread || markingUnread) return;
    setMarkingUnread(true);
    try {
      const response = await fetch(
        `/api/sms-messages/mark-unread?phoneNumber=${encodeURIComponent(phone)}`,
        { method: "PATCH" }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.error || "Failed to mark as unread");
        return;
      }
      await refreshLoadedConversations({ filter: inboxListFilter });
      setThreadMessages((prev) => {
        const idx = prev.findIndex((m) => m.received);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], read: false };
        return next;
      });
    } catch (error) {
      console.error("Mark unread failed:", error);
      alert("Failed to mark as unread");
    } finally {
      setMarkingUnread(false);
    }
  }

  async function updateSmsReadState({
    read,
    phoneNumbers,
  }: {
    read: boolean;
    phoneNumbers: string[];
  }) {
    const response = await fetch("/api/sms-messages/read-state", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        read,
        phoneNumbers,
      }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || "Failed to update read state");
    }
  }

  async function handleConversationBulkRead({ read }: { read: boolean }) {
    if (selectedConversationPhones.size === 0 || bulkUpdating) return;
    setBulkUpdating(true);
    try {
      await updateSmsReadState({
        read,
        phoneNumbers: Array.from(selectedConversationPhones),
      });
      setSelectedConversationPhones(new Set());
      await refreshLoadedConversations({ filter: inboxListFilter });
    } catch (error) {
      console.error("Bulk conversation read update failed:", error);
      alert(
        read ? "Failed to mark as read" : "Failed to mark as unread"
      );
    } finally {
      setBulkUpdating(false);
    }
  }

  function handleSelectVisibleConversations() {
    setSelectedConversationPhones(
      new Set(conversations.map((c) => c.phoneNumber))
    );
  }

  function handleToggleSelectVisibleConversations() {
    if (conversationSelectAllState === true) {
      setSelectedConversationPhones(new Set());
      return;
    }
    handleSelectVisibleConversations();
  }

  async function handleSelectAllConversations() {
    if (selectingAllConversations) return;
    setSelectingAllConversations(true);
    try {
      const params = new URLSearchParams({
        listFilter: inboxListFilter,
        phonesOnly: "true",
      });
      const trimmedSearch = appliedConversationSearch.trim();
      if (trimmedSearch) {
        params.set("search", trimmedSearch);
      }
      const response = await fetch(`/api/sms-messages?${params}`);
      if (!response.ok) {
        alert("Failed to select all conversations");
        return;
      }
      const data = await response.json();
      const phoneNumbers: string[] = data.phoneNumbers || [];
      setSelectedConversationPhones(new Set(phoneNumbers));
    } catch (error) {
      console.error("Select all conversations failed:", error);
      alert("Failed to select all conversations");
    } finally {
      setSelectingAllConversations(false);
    }
  }

  const handleTabChange = (value: string) => {
    if (value === "campaigns") {
      navigate("/messages/sms/campaigns");
    } else if (value === "settings") {
      navigate("/messages/sms/settings");
    }
  };

  const currentTab = location.pathname.includes("/campaigns")
    ? "campaigns"
    : location.pathname.includes("/settings")
      ? "settings"
      : "inbox";

  function applyConversationSearch() {
    setAppliedConversationSearch(conversationSearch.trim());
  }

  const canMarkConversationUnread = useMemo(
    () => threadMessages.some((m) => m.received),
    [threadMessages]
  );

  const conversationSelectAllState = useMemo(() => {
    if (conversations.length === 0) return false;
    const selectedCount = conversations.filter((c) =>
      selectedConversationPhones.has(c.phoneNumber)
    ).length;
    if (selectedCount === 0) return false;
    if (selectedCount === conversations.length) return true;
    return "indeterminate" as const;
  }, [conversations, selectedConversationPhones]);

  const selectedConversation =
    selectedPhoneNumber != null && selectedPhoneNumber !== ""
      ? {
          phoneNumber: selectedPhoneNumber,
          messages: threadMessages,
        }
      : undefined;

  useEffect(() => {
    if (!threadLoading) {
      messagesEndRef.current?.scrollIntoView();
    }
  }, [selectedPhoneNumber, threadMessages.length, threadLoading]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 64px)' }}>
      <Tabs 
        value={currentTab}
        onValueChange={handleTabChange}
        className="w-full h-full flex flex-col"
      >
        <div className="p-4 border-b flex-shrink-0">
          <TabsList>
            <TabsTrigger value="inbox">Inbox</TabsTrigger>
            <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="inbox" className="flex-1 flex overflow-hidden m-0 min-h-0">
          <div className="flex w-full h-full min-h-0">
            {isListOnly ? (
            <div className="flex flex-col flex-1 min-h-0 min-w-0 w-full">
              <div className="p-2 pb-2 border-b flex-shrink-0">
                <div className="flex w-full justify-center">
                  <Tabs
                    value={inboxListFilter}
                    onValueChange={(value) =>
                      setInboxListFilter(value as InboxListFilter)
                    }
                    className="w-full max-w-sm gap-1.5"
                  >
                    <TabsList className="grid h-8 w-full grid-cols-3">
                      <TabsTrigger value="unread" className="text-xs px-2">
                        Unread
                      </TabsTrigger>
                      <TabsTrigger value="all" className="text-xs px-2">
                        All
                      </TabsTrigger>
                      <TabsTrigger value="optouts" className="text-xs px-2">
                        Opt-outs
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                <div className="mt-2 flex gap-2">
                  <div className="relative min-w-0 flex-1">
                    <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                      type="search"
                      value={conversationSearch}
                      onChange={(e) => setConversationSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyConversationSearch();
                        }
                      }}
                      placeholder="Search by name or number…"
                      className="h-9 pl-8"
                      aria-label="Search conversations"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 shrink-0"
                    onClick={applyConversationSearch}
                    disabled={
                      conversationSearch.trim() ===
                      appliedConversationSearch.trim()
                    }
                  >
                    Search
                  </Button>
                </div>
                {conversations.length > 0 ? (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-1">
                      <Checkbox
                        checked={conversationSelectAllState}
                        onCheckedChange={handleToggleSelectVisibleConversations}
                        aria-label="Select all conversations"
                        disabled={
                          conversations.length === 0 ||
                          selectingAllConversations
                        }
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="p-0.5 hover:bg-accent rounded-sm"
                            aria-label="Selection options"
                            disabled={selectingAllConversations}
                          >
                            {selectingAllConversations ? (
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-3 w-3 text-muted-foreground" />
                            )}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem
                            onClick={handleSelectVisibleConversations}
                            disabled={conversations.length === 0}
                          >
                            Select Visible
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => void handleSelectAllConversations()}
                            disabled={selectingAllConversations}
                          >
                            Select All
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <InboxReadActionsMenu
                      disabled={selectedConversationPhones.size === 0}
                      updating={bulkUpdating}
                      onMarkRead={() =>
                        void handleConversationBulkRead({ read: true })
                      }
                      onMarkUnread={() =>
                        void handleConversationBulkRead({ read: false })
                      }
                    />
                    <span className="text-sm text-muted-foreground">
                      {selectedConversationPhones.size} selected
                    </span>
                  </div>
                ) : null}
              </div>
              <div
                ref={conversationListScrollRef}
                className="flex-1 overflow-y-auto min-h-0 p-2 relative"
              >
              {conversationListLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-sm text-muted-foreground px-2 py-4">
                  {appliedConversationSearch
                    ? "No conversations match your search"
                    : inboxListFilter === "unread"
                      ? "No unread conversations"
                      : inboxListFilter === "optouts"
                        ? "No opt-outs"
                        : "No conversations"}
                </div>
              ) : (
                <>
                  <div className="space-y-1">
                    {conversations.map((conversation) => {
                        const lead = conversation.lead;
                        const leadName = lead
                          ? `${lead.firstName || ""} ${lead.lastName || ""}`.trim()
                          : null;
                        const lastAtLabel = formatConversationLastAt(
                          conversation.lastAt
                        );

                        return (
                          <div
                            key={conversation.phoneNumber}
                            className="flex items-start gap-2 px-2 py-2 rounded-md hover:bg-muted transition-colors"
                          >
                            <Checkbox
                              className="mt-0.5"
                              checked={selectedConversationPhones.has(
                                conversation.phoneNumber
                              )}
                              onCheckedChange={() => {
                                setSelectedConversationPhones((prev) =>
                                  toggleSetValue({
                                    current: prev,
                                    value: conversation.phoneNumber,
                                  })
                                );
                              }}
                              aria-label={`Select ${leadName || conversation.phoneNumber}`}
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                navigate(
                                  `/messages/sms/inbox/${encodeURIComponent(conversation.phoneNumber)}`
                                );
                                const res = await fetch(
                                  `/api/sms-messages?phoneNumber=${encodeURIComponent(conversation.phoneNumber)}`,
                                  { method: "PATCH" }
                                );
                                if (res.ok) {
                                  await refreshLoadedConversations({
                                    filter: inboxListFilter,
                                  });
                                }
                              }}
                              className="min-w-0 flex-1 text-left"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-medium text-sm truncate min-w-0">
                                  {leadName
                                    ? `${leadName} (${conversation.phoneNumber})`
                                    : conversation.phoneNumber}
                                </div>
                                {conversation.hasUnread ? (
                                  <span className="shrink-0 text-xs font-medium text-primary">
                                    Unread
                                  </span>
                                ) : null}
                              </div>
                              {lastAtLabel ? (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {lastAtLabel}
                                </div>
                              ) : null}
                              {conversation.lastBody ? (
                                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                  {conversation.lastBody}
                                </div>
                              ) : null}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  {hasMoreConversations ? (
                    <div ref={loadMoreSentinelRef} className="h-1" />
                  ) : null}
                  {loadingMoreConversations ? (
                    <div className="flex justify-center py-3">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : null}
                </>
              )}
              </div>
            </div>
            ) : null}

          {!isListOnly ? (
          <div className="flex-1 flex flex-col overflow-hidden min-w-0 w-full">
            {selectedConversation ? (
              <>
                <div className="border-b px-4 py-2 flex-shrink-0 flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    aria-label="Back to conversations"
                    onClick={() => navigate("/messages/sms/inbox")}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex flex-1 min-w-0 items-center justify-between gap-2">
                    {(() => {
                      const lead =
                        selectedConversation.messages.find((msg) => msg.lead)
                          ?.lead ??
                        conversations.find(
                          (c) =>
                            c.phoneNumber === selectedConversation.phoneNumber
                        )?.lead;
                      const leadName = lead
                        ? `${lead.firstName || ""} ${lead.lastName || ""}`.trim()
                        : null;
                      const leadId =
                        selectedConversation.messages.find((m) => m.contactLead)
                          ?.contactLead ??
                        conversations.find(
                          (c) =>
                            c.phoneNumber === selectedConversation.phoneNumber
                        )?.contactLead;
                      const markUnreadDisabled =
                        !canMarkConversationUnread ||
                        markingUnread ||
                        threadLoading;
                      return (
                        <>
                          <div className="min-w-0 flex items-center">
                            <h2 className="font-semibold leading-snug">
                              {leadName
                                ? `${leadName} (${selectedConversation.phoneNumber})`
                                : selectedConversation.phoneNumber}
                            </h2>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="shrink-0"
                                aria-label="Conversation actions"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem
                                disabled={markUnreadDisabled}
                                onSelect={() => void handleMarkUnread()}
                              >
                                {markingUnread ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Mail className="h-4 w-4" />
                                )}
                                Mark unread
                              </DropdownMenuItem>
                              {leadId ? (
                                <DropdownMenuItem asChild>
                                  <Link to={`/contacts/${leadId}/edit`}>
                                    <ExternalLink className="h-4 w-4" />
                                    View contact
                                  </Link>
                                </DropdownMenuItem>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <TooltipProvider delayDuration={200}>
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                    {threadLoading ? (
                      <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <>
                        {[...selectedConversation.messages]
                          .reverse()
                          .map((message) => (
                            <div
                              key={message._id}
                              className={`flex ${
                                message.received
                                  ? "justify-start"
                                  : "justify-end"
                              }`}
                            >
                              <div
                                className={`max-w-[70%] rounded-lg p-3 ${
                                  message.received
                                    ? "bg-muted"
                                    : "bg-primary text-primary-foreground"
                                }`}
                              >
                                <div className="text-sm whitespace-pre-wrap">
                                  {message.body}
                                </div>
                                <div
                                  className={`text-xs mt-1 flex items-center gap-1.5 ${
                                    message.received
                                      ? "justify-start text-muted-foreground"
                                      : "justify-end text-primary-foreground/70"
                                  }`}
                                >
                                  {message.received && (
                                    <SMSMessageStatusIcon
                                      message={message}
                                      variant="received"
                                    />
                                  )}
                                  <span>
                                    {new Date(
                                      message.createdAt
                                    ).toLocaleString()}
                                  </span>
                                  {!message.received && (
                                    <SMSMessageStatusIcon
                                      message={message}
                                      variant="sent"
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>
                </TooltipProvider>
                <div className="border-t p-4 flex-shrink-0">
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Type a message..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      className="min-h-9 resize-none"
                      disabled={isSending}
                    />
                    <Button
                      onClick={handleSend}
                      disabled={!replyText.trim() || isSending}
                      size="icon"
                      className="h-9 w-9 shrink-0"
                    >
                      {isSending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
          ) : null}
        </div>
      </TabsContent>
    </Tabs>
    </div>
  );
}
